import { Component } from 'react';
import { isChunkLoadError } from '../lib/lazyWithReload';
import i18n from '../lib/i18n';

const RELOAD_KEY = 'zb-chunk-reloaded';

// Chrome's page translator marks the document and wraps every text node in
// <font class="notranslate">. A crash on a translated page is almost always
// the translator fighting React (lib/domGuard.js absorbs the common case) —
// tell the user how to stop it instead of leaving them with a bare DOM error.
const isPageTranslated = () => {
  try {
    const html = document.documentElement;
    return html.classList.contains('translated-ltr')
      || html.classList.contains('translated-rtl')
      || !!document.querySelector('font.notranslate, #goog-gt-tt, .goog-te-banner-frame');
  } catch { return false; }
};

// Catches render errors so the user sees a helpful message instead of a blank
// white screen, and we get the actual error text to fix the root cause.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A new deploy can leave this tab requesting chunk files that no longer
    // exist. Reload once (network-first SW fetches the fresh build); the flag
    // stops an infinite loop if the chunk is genuinely unreachable.
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
      return;
    }
    // Surface it in the console for debugging.
    console.error('App crash captured by ErrorBoundary:', error, info);
  }

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      const translated = isPageTranslated();
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6 font-sans">
          <div className="max-w-lg w-full bg-white border border-red-200 rounded-2xl shadow-sm p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4 text-2xl">!</div>
            <h1 className="text-xl font-bold text-[#1d2d3d] mb-2">{i18n.t('errorBoundary.heading')}</h1>
            <p className="text-sm text-[#6d7d8b] mb-4">
              {i18n.t('errorBoundary.body')}
            </p>
            <pre className="text-left text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 overflow-auto max-h-40 mb-5 whitespace-pre-wrap break-words">{msg}</pre>
            {translated && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-left">
                {i18n.t('errorBoundary.translateHint')}
              </p>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => { this.setState({ error: null }); window.location.reload(); }}
                className="h-11 px-5 rounded-xl bg-[#1a4f8b] hover:bg-[#123963] text-white font-semibold"
              >
                {i18n.t('errorBoundary.reload')}
              </button>
              <button
                onClick={() => { window.location.href = '/dashboard'; }}
                className="h-11 px-5 rounded-xl border border-[#d6dce2] text-[#1d2d3d] font-semibold hover:bg-gray-50"
              >
                {i18n.t('errorBoundary.goToDashboard')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
